/* eslint-disable @typescript-eslint/no-empty-function */
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Alert,
} from "@mui/material";

// ✅ Type definitions
interface PaymentError {
  code: string;
  detailed_code: string;
  reason: string;
  requirement: string;
}

interface PaymentVerificationData {
  alternatives: unknown[];
  current_deadline: number | null;
  currently_due: string[];
  disabled_reason: string;
  errors: PaymentError[];
  eventually_due: string[];
  past_due: string[];
  pending_verification: string[];
}

interface PaymentVerificationErrorDialogProps {
  open: boolean;
  onClose: () => void;
  errorData: PaymentVerificationData;
  onProceed?: () => void;
}

// ✅ Mapping requirement keys to user-friendly labels
const requirementToFieldName: Record<string, string> = {
  "individual.address.first_name": "First Name",
  "individual.address.last_name": "Last Name",
  "individual.address.city": "City",
  "individual.dob": "Birthday",
  "individual.phone": "Phone Number",
  "individual.email": "Email",
  "individual.address.line1": "Address Line 1",
  "individual.address.line2": "Address Line 2",
  "individual.address.postal_code": "Zip Code",
  "individual.address.state": "State",
  "individual.id_number": "Social Security Number",
  "individual.verification.document": "Identity document",
  "individual.verification.document.front": "Identity Document (Front)",
  "individual.verification.document.back": "Identity Document (Back)",

  // Bank / external account fields
  "external_account": "Bank Account",
  "external_accounts": "Bank Accounts",
  "external_account.routing_number": "Bank Routing Number",
  "external_account.account_number": "Bank Account Number",
  "documents.bank_account_ownership_verification.files": "Bank Statement",
};
const personalFields = [
  "first_name",
  "last_name",
  "dob",
  "phone",
  "email",
  "address.line1",
  "address.line2",
  "address.city",
  "address.state",
  "address.postal_code",
  "id_number",
  "verification.document",
  "verification.document.front",
  "verification.document.back",
];

export const getPaymentRequirementsStatus = (errorData: PaymentVerificationData) => {
  const combinedRequirements = [
    ...(errorData?.currently_due || []),
    ...(errorData?.pending_verification || []),
  ];

  const individualPersonalFields = personalFields.map(pf => `individual.${pf}`);

  const hasBankFieldsDue = combinedRequirements.some(req =>
    ["external_account", "external_account_verification_document", "documents.bank_account_ownership_verification.files"].includes(req)
  );

  const hasProfileFieldsDue = combinedRequirements.some(field =>
    individualPersonalFields.includes(field) || field.startsWith("individual.verification.")
  );

  return { hasBankFieldsDue, hasProfileFieldsDue };
};

const PaymentVerificationErrorDialog: React.FC<PaymentVerificationErrorDialogProps> = ({
  open,
  onClose,
  errorData,
  onProceed,
}) => {
  const isEmpty = !errorData || (!errorData.errors?.length && !errorData.currently_due?.length);

  const paymentStatus = React.useMemo(() => getPaymentRequirementsStatus(errorData || {}), [errorData]);

  const itemsToDisplay = React.useMemo(() => {
    if (!errorData) return [];
    return [
      ...(errorData.currently_due || []),
      ...(errorData.pending_verification || []),
    ].map(requirement => ({ requirement }));
  }, [errorData]);

  const handleProceed = () => {
    if (onProceed) onProceed();
    onClose();
  };

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleDialogClose = (_event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", width: "520px", padding: 0 } }}
    >
      <DialogContent sx={{ padding: "16px", textAlign: "center" }}>
        {isEmpty ? (
          <Typography variant="body1">
            Your information needs to be reviewed before proceeding.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" fontWeight={600} color="warning.main">
              Action Required
            </Typography>

            <Typography variant="body2" sx={{ textAlign: "left", pl: 1.5 }}>
              Some of your account information needs to be updated before you can receive your payments.
            </Typography>

            <Typography variant="body2">
              Please review and correct the items below and then resubmit.
            </Typography>

            {/* Profile fields */}
            {paymentStatus.hasProfileFieldsDue && (
              <>
                <Typography variant="subtitle2" sx={{ textAlign: "left", pl: 1.5, fontWeight: 600 }}>
                  Personal Information:
                </Typography>
                <List sx={{ listStyleType: "disc", pl: 4, m: 0, color: "#d32f2f" }}>
                  {itemsToDisplay
                    .filter(item => item.requirement.startsWith("individual."))
                    .map((item, index) => {
                      const fieldName = requirementToFieldName[item.requirement] || item.requirement;
                      return (
                        <ListItem key={index} sx={{ display: "list-item", py: 0.5 }}>
                          <ListItemText
                            primaryTypographyProps={{ variant: "body2", sx: { color: "#d32f2f", fontWeight: 600 } }}
                            primary={fieldName}
                          />
                        </ListItem>
                      );
                    })}
                </List>
              </>
            )}

            {/* Bank fields */}
            {paymentStatus.hasBankFieldsDue && (
              <>
                <Typography variant="subtitle2" sx={{ textAlign: "left", pl: 1.5, fontWeight: 600 }}>
                  Bank Information:
                </Typography>
                <List sx={{ listStyleType: "disc", pl: 4, m: 0, color: "#d32f2f" }}>
                  {itemsToDisplay
                    .filter(item =>
                      ["external_account", "external_account_verification_document", "documents.bank_account_ownership_verification.files"].includes(item.requirement)
                    )
                    .map((item, index) => {
                      const fieldName = requirementToFieldName[item.requirement] || item.requirement;
                      return (
                        <ListItem key={index} sx={{ display: "list-item", py: 0.5 }}>
                          <ListItemText
                            primaryTypographyProps={{ variant: "body2", sx: { color: "#d32f2f", fontWeight: 600 } }}
                            primary={fieldName}
                          />
                        </ListItem>
                      );
                    })}
                </List>
              </>
            )}

            <Typography variant="body2" sx={{ fontSize: "12.5px", color: "black" }}>
              We have also sent this notice to your registered school email address.
            </Typography>

           <Alert
  severity="warning"
  icon={false}
  sx={{
    backgroundColor: "transparent",   // no background
    border: "none",                   // no border
    boxShadow: "none",
   
    px: 1,
    py: 1,
    color: "inherit",
  }}
>
  <Typography
    variant="caption"
    sx={{
      fontSize: "11px",
      textAlign: "left",
      color: "#333",     // clean dark text
      fontWeight: 500,
      display: "block",
      lineHeight: 1.4,
    }}
  >
    <strong>Note:</strong> For security reasons, myNilPay does not store any sensitive data — 
    you may need to reenter it.
  </Typography>
</Alert>




          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "12px 16px", justifyContent: "center" }}>
        <Button
          onClick={handleProceed}
          autoFocus
          sx={{
            fontSize: "0.9rem",
            backgroundColor: "#592EE5",
            color: "white",
            textTransform: "none",
            borderRadius: "8px",
            px: 4,
            py: 1,
            '&:hover': { backgroundColor: '#4a25d4' },
          }}
        >
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentVerificationErrorDialog;
